import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, Trash2, Calendar, Thermometer, User, Edit, Home, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { format } from 'date-fns';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useAuth } from '../context/AuthContext';
import { PhotoThumb } from '../components/PhotoUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InspectionPhoto = ({ path }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  
  useEffect(() => {
    let cancelled = false;
    const fetchImage = async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('crewToken');
        const res = await axios.get(`${API}/files/${path}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        if (!cancelled) setBlobUrl(URL.createObjectURL(res.data));
      } catch {}
    };
    if (path && path.includes('/')) fetchImage();
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [path]);
  
  if (!blobUrl) return <div className="w-full h-full bg-stone-100 animate-pulse" />;
  return <img src={blobUrl} alt="Inspection photo" className="w-full h-full object-cover" />;
};

const InspectionView = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const inspectionId = params.inspectionId || searchParams.get('inspectionId');
  const navigate = useNavigate();
  const { isCrew } = useAuth();
  const [inspection, setInspection] = useState(null);
  const [hive, setHive] = useState(null);
  const [yardLocation, setYardLocation] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [inspectionId]);

  const fetchData = async () => {
    try {
      const inspectionRes = await axios.get(`${API}/inspections/${inspectionId}`);
      const inspection = inspectionRes.data;
      setInspection(inspection);
      const hiveRes = await axios.get(`${API}/hives/${inspection.hive_id}`);
      setHive(hiveRes.data);
      // Fetch yard location
      if (hiveRes.data.yard) {
        try {
          const token = localStorage.getItem('token') || localStorage.getItem('crewToken');
          const yardsRes = await axios.get(`${API}/yards`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          const yard = yardsRes.data.find(y => y.name === hiveRes.data.yard || y.name?.trim() === hiveRes.data.yard);
          if (yard?.location) setYardLocation(yard.location);
        } catch {}
      }
    } catch (error) {
      toast.error('Failed to load inspection');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this inspection?\n\nThis action cannot be undone.'
    );
    
    if (confirmDelete) {
      try {
        await axios.delete(`${API}/inspections/${inspectionId}`);
        toast.success('Inspection deleted successfully');
        navigate(isCrew ? '/crew-dashboard' : `/?view=hive-detail&hiveId=${inspection.hive_id}`);
      } catch (error) {
        toast.error('Failed to delete inspection');
      }
    }
  };

  const exportToPDF = async () => {
    setExporting(true);
    try {
      const reportElement = document.getElementById('inspection-report');
      const pageWidth = 210;
      const pageHeight = 297;
      const marginX = 15;
      const marginY = 15;
      const printableWidth = pageWidth - (marginX * 2);
      const printableHeight = pageHeight - (marginY * 2);
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const sections = reportElement.children;
      let currentY = marginY;
      
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        const sectionCanvas = await html2canvas(section, {
          scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff', windowWidth: 800,
        });
        const sectionImgData = sectionCanvas.toDataURL('image/png');
        const sectionWidth = printableWidth;
        const sectionHeight = (sectionCanvas.height * sectionWidth) / sectionCanvas.width;
        const spaceLeft = (pageHeight - marginY) - currentY;
        
        if (sectionHeight > spaceLeft && currentY > marginY) {
          pdf.addPage();
          currentY = marginY;
        }
        
        if (sectionHeight > printableHeight) {
          let sectionYPos = 0;
          const scale = sectionWidth / sectionCanvas.width;
          while (sectionYPos < sectionHeight) {
            if (sectionYPos > 0) { pdf.addPage(); currentY = marginY; }
            const remainingPageSpace = printableHeight - (currentY - marginY);
            const sliceHeightMM = Math.min(remainingPageSpace, sectionHeight - sectionYPos);
            const sliceHeightPx = sliceHeightMM / scale;
            const sourceYPx = sectionYPos / scale;
            const sliceCanvas = document.createElement('canvas');
            sliceCanvas.width = sectionCanvas.width;
            sliceCanvas.height = Math.ceil(sliceHeightPx);
            const ctx = sliceCanvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
            ctx.drawImage(sectionCanvas, 0, sourceYPx, sectionCanvas.width, sliceHeightPx, 0, 0, sectionCanvas.width, sliceHeightPx);
            pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', marginX, currentY, sectionWidth, sliceHeightMM);
            sectionYPos += sliceHeightMM;
            currentY += sliceHeightMM;
            if (currentY >= pageHeight - marginY - 5 && sectionYPos < sectionHeight) {
              pdf.addPage(); currentY = marginY;
            }
          }
        } else {
          pdf.addImage(sectionImgData, 'PNG', marginX, currentY, sectionWidth, sectionHeight);
          currentY += sectionHeight + 4;
        }
      }

      pdf.save(`Hive-${hive.hive_number}-Inspection-${format(new Date(inspection.date), 'yyyy-MM-dd')}.pdf`);
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-stone-600 text-lg">Loading...</div>
      </div>
    );
  }

  if (!inspection || !hive) {
    return (
      <div className="flex flex-col items-center justify-center p-4">
        <p className="text-stone-600 text-lg mb-4">Inspection not found</p>
        <Button onClick={() => navigate('/')}>Back to Dashboard</Button>
      </div>
    );
  }

  return (
    <div className="bg-stone-50 pb-24">
      <div className="bg-white border-b border-stone-200 sticky top-0 z-40 shadow-sm no-print">
        <div className="max-w-4xl mx-auto p-4 flex items-center justify-between">
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(isCrew ? '/crew-dashboard' : '/')}
              data-testid="home-btn"
              className="h-12 w-12"
              title="Home"
            >
              <Home className="h-6 w-6" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(isCrew ? '/crew-dashboard' : `/?view=hive-detail&hiveId=${inspection.hive_id}`)}
              data-testid="back-btn"
              className="h-12 w-12"
              title={isCrew ? "Back to Dashboard" : "Back to Hive"}
            >
              <ArrowLeft className="h-6 w-6" />
            </Button>
          </div>
          <h1 className="text-xl font-bold text-stone-800 font-heading">Inspection Report</h1>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => navigate(`/?view=edit-inspection&hiveId=${inspection.hive_id}&inspectionId=${inspectionId}`)}
              data-testid="edit-inspection-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full h-12 px-6 font-bold"
            >
              <Edit className="mr-2 h-5 w-5" />
              Edit
            </Button>
            <Button
              type="button"
              onClick={exportToPDF}
              disabled={exporting}
              data-testid="export-pdf-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-full h-12 px-6 font-bold"
            >
              <Download className="mr-2 h-5 w-5" />
              {exporting ? 'Exporting...' : 'PDF'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDelete();
              }}
              data-testid="delete-inspection-btn"
              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-12 w-12"
            >
              <Trash2 className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      <div id="inspection-report" className="max-w-4xl mx-auto p-4 md:p-8 space-y-6">
        <div className="bg-white border-2 border-amber-500 rounded-2xl p-8 shadow-md">
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-extrabold text-stone-800 font-heading mb-2">
              Beehive Inspection Report
            </h1>
            <p className="text-xl text-amber-600 font-bold">Hive {hive.hive_number}</p>
            {hive.yard && (
              <div className="mt-1">
                <p className="text-stone-600 font-medium" data-testid="report-apiary-name">{hive.yard}</p>
                {yardLocation && (
                  <p className="text-stone-500 text-sm" data-testid="report-apiary-location">{yardLocation}</p>
                )}
              </div>
            )}
          </div>

          <Separator className="my-6" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InfoCard icon={<Calendar />} label="Date" value={format(new Date(inspection.date), 'PPP')} />
            {inspection.time && <InfoCard icon={<Calendar />} label="Time" value={inspection.time} />}
            {inspection.beekeeper && <InfoCard icon={<User />} label="Beekeeper" value={inspection.beekeeper} />}
            {inspection.temperature && <InfoCard icon={<Thermometer />} label="Temperature" value={inspection.temperature} />}
            {yardLocation && <InfoCard icon={<MapPin />} label="Apiary Location" value={yardLocation} />}
          </div>

          {inspection.weather && inspection.weather.length > 0 && (
            <div className="mt-6">
              <div className="text-sm text-stone-600 font-medium mb-2">Weather Conditions</div>
              <div className="flex flex-wrap gap-2">
                {inspection.weather.map((w) => (
                  <span key={w} className="bg-stone-100 text-stone-700 px-3 py-1 rounded-full text-sm">
                    {w}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {inspection.inspection_type && inspection.inspection_type.length > 0 && (
          <ReportSection title="Inspection Overview">
            <DataRow label="Type of Inspection" value={inspection.inspection_type.join(', ')} />
            {inspection.activity_entrance && (Array.isArray(inspection.activity_entrance) ? inspection.activity_entrance.length > 0 : inspection.activity_entrance) && (
              <DataRow 
                label="Activity at Entrance" 
                value={Array.isArray(inspection.activity_entrance) 
                  ? inspection.activity_entrance.join(', ') 
                  : inspection.activity_entrance} 
              />
            )}
            {inspection.hive_condition && <DataRow label="Hive Condition" value={inspection.hive_condition} />}
            {inspection.repairs_needed && <DataRow label="Repairs Needed" value={inspection.repairs_needed} />}
            {inspection.colony_mood && <DataRow label="Colony Mood" value={inspection.colony_mood} />}
          </ReportSection>
        )}

        {(() => {
          // Get frame data from frame_data dict or directly from inspection keys (backwards compat)
          const fd = inspection.frame_data || {};
          const allFrameKeys = { ...fd };
          // Also check for any box_frames_/legacy_box_ directly on inspection (old data)
          Object.keys(inspection).forEach(k => {
            if ((k.startsWith('box_frames_') || k.startsWith('legacy_box_')) && !allFrameKeys[k]) {
              allFrameKeys[k] = inspection[k];
            }
          });
          
          const hasFrameData = (inspection.bottom_brood_box && inspection.bottom_brood_box.some(f => f))
            || (inspection.top_brood_box && inspection.top_brood_box.some(f => f))
            || Object.keys(allFrameKeys).some(k => Array.isArray(allFrameKeys[k]) && allFrameKeys[k].some(f => f))
            || (inspection.brood && inspection.brood.length > 0)
            || (inspection.pattern && (Array.isArray(inspection.pattern) ? inspection.pattern.length > 0 : !!inspection.pattern))
            || inspection.queen_spotted || inspection.queen_cells || inspection.population || inspection.swarm_conditions;
          if (!hasFrameData) return null;
          return (
        <ReportSection title="Frame-by-Frame Observations">
          {inspection.bottom_brood_box && inspection.bottom_brood_box.some(f => f) && (
            <FrameBoxDisplay label="Bottom Brood Box" frames={inspection.bottom_brood_box} />
          )}

          {inspection.top_brood_box && inspection.top_brood_box.some(f => f) && (
            <FrameBoxDisplay label="Top Brood Box" frames={inspection.top_brood_box} />
          )}

          {/* Dynamic box frames from frame_data dict */}
          {Object.keys(allFrameKeys)
            .filter(k => k.startsWith('box_frames_'))
            .sort((a, b) => {
              const aIsBrood = a.includes('brood');
              const bIsBrood = b.includes('brood');
              if (aIsBrood && !bIsBrood) return -1;
              if (!aIsBrood && bIsBrood) return 1;
              return a.localeCompare(b);
            })
            .map(key => {
              const frames = allFrameKeys[key];
              if (!Array.isArray(frames) || !frames.some(f => f)) return null;
              const label = key.replace('box_frames_', '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              return <FrameBoxDisplay key={key} label={label} frames={frames} />;
            })
          }

          {/* Legacy box frames */}
          {Object.keys(allFrameKeys)
            .filter(k => k.startsWith('legacy_box_'))
            .sort((a, b) => a.localeCompare(b))
            .map(key => {
              const frames = allFrameKeys[key];
              if (!Array.isArray(frames) || !frames.some(f => f)) return null;
              const idx = parseInt(key.replace('legacy_box_', ''));
              const label = `Box ${idx + 1}`;
              return <FrameBoxDisplay key={key} label={label} frames={frames} />;
            })
          }

          {inspection.brood && inspection.brood.length > 0 && <DataRow label="Brood" value={inspection.brood.join(', ')} />}
          {inspection.pattern && (Array.isArray(inspection.pattern) ? inspection.pattern.length > 0 : inspection.pattern) && (
            <DataRow 
              label="Pattern" 
              value={Array.isArray(inspection.pattern) 
                ? inspection.pattern.join(', ') 
                : inspection.pattern} 
            />
          )}
          
          {inspection.queen_spotted && (
            <div className="bg-lime-50 border border-lime-200 rounded-xl p-4 mt-4">
              <div className="font-bold text-lime-700 text-lg mb-2">✓ Queen Spotted</div>
              {inspection.queen_frame && <div className="text-stone-700">Frame: {inspection.queen_frame}</div>}
              {inspection.queen_marked && <div className="text-stone-700">Marked: Yes</div>}
              {inspection.queen_color && <div className="text-stone-700">Color: {inspection.queen_color}</div>}
              {inspection.queen_age && <div className="text-stone-700">Age: {inspection.queen_age} years</div>}
            </div>
          )}

          {inspection.queen_cells && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
              <div className="font-bold text-amber-700 text-lg mb-2">⚠ Queen Cells Present</div>
              {inspection.queen_cells_frame && <div className="text-stone-700">Frame: {inspection.queen_cells_frame}</div>}
            </div>
          )}

          {inspection.population && <DataRow label="Population" value={inspection.population} />}
          
          {inspection.swarm_conditions && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-4">
              <div className="font-bold text-red-700 text-lg">⚠ Swarm Conditions Detected</div>
            </div>
          )}
        </ReportSection>
          );
        })()}

        {(() => {
          const hasHealthData = (inspection.hive_condition_status && inspection.hive_condition_status.length > 0)
            || (inspection.diseases && inspection.diseases.length > 0)
            || (inspection.pests && inspection.pests.length > 0);
          if (!hasHealthData) return null;
          return (
        <ReportSection title="Hive Condition & Health">
          {inspection.hive_condition_status && inspection.hive_condition_status.length > 0 && (
            <DataRow 
              label="Condition" 
              value={Array.isArray(inspection.hive_condition_status) 
                ? inspection.hive_condition_status.join(', ') 
                : inspection.hive_condition_status} 
            />
          )}
          
          {inspection.diseases && inspection.diseases.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="font-bold text-red-700 mb-2">Diseases Detected</div>
              <div className="text-stone-700">{inspection.diseases.join(', ')}</div>
            </div>
          )}

          {inspection.pests && inspection.pests.length > 0 && (
            <div>
              <DataRow label="Pests Observed" value={inspection.pests.join(', ')} />
              {inspection.pests_other && <DataRow label="Other Pests" value={inspection.pests_other} />}
            </div>
          )}
        </ReportSection>
          );
        })()}

        {(() => {
          const hasFeedingData = inspection.nectar_level || inspection.pollen_level || inspection.honey_brood_level
            || (inspection.honey_frames && inspection.honey_frames.length > 0)
            || inspection.feeding || (inspection.feed_type && inspection.feed_type.length > 0) || inspection.feeder_type;
          if (!hasFeedingData) return null;
          return (
        <ReportSection title="Resources & Feeding">
          <div className="grid grid-cols-3 gap-4">
            {inspection.nectar_level && <DataRow label="Nectar" value={inspection.nectar_level} />}
            {inspection.pollen_level && <DataRow label="Pollen" value={inspection.pollen_level} />}
            {inspection.honey_brood_level && <DataRow label="Honey Around Brood" value={inspection.honey_brood_level} />}
          </div>

          {inspection.honey_frames && inspection.honey_frames.length > 0 && (
            <DataRow label="Honey Frames in Super" value={`Frames: ${inspection.honey_frames.join(', ')}`} />
          )}

          {inspection.feeding && <DataRow label="Feeding Status" value={inspection.feeding} />}
          {inspection.feed_type && inspection.feed_type.length > 0 && <DataRow label="Feed Type" value={inspection.feed_type.join(', ')} />}
          {inspection.feeder_type && <DataRow label="Feeder Type" value={inspection.feeder_type} />}
        </ReportSection>
          );
        })()}

        {/* Hive Weight Section */}
        {(inspection.hive_weight || inspection.baseline_weight) && (
          <ReportSection title="Hive Weight">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {inspection.hive_weight && (
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <div className="text-xs text-stone-500 font-medium uppercase mb-1">Current Weight</div>
                  <div className="text-2xl font-bold text-amber-700">{inspection.hive_weight} kg</div>
                </div>
              )}
              {inspection.baseline_weight && (
                <div className="bg-stone-100 rounded-xl p-4 text-center">
                  <div className="text-xs text-stone-500 font-medium uppercase mb-1">Baseline (Tare)</div>
                  <div className="text-2xl font-bold text-stone-700">{inspection.baseline_weight} kg</div>
                </div>
              )}
              {inspection.hive_weight && inspection.baseline_weight && (
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <div className="text-xs text-stone-500 font-medium uppercase mb-1">Estimated Stores</div>
                  <div className="text-2xl font-bold text-green-700">
                    {(parseFloat(inspection.hive_weight) - parseFloat(inspection.baseline_weight)).toFixed(1)} kg
                  </div>
                </div>
              )}
            </div>
            {inspection.weight_loss_reasons && inspection.weight_loss_reasons.length > 0 && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                <div className="text-sm font-medium text-yellow-800 mb-2">Weight Decline Reasons Noted:</div>
                <div className="flex flex-wrap gap-2">
                  {inspection.weight_loss_reasons.map((reason) => (
                    <span key={reason} className="bg-yellow-200 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                      {reason}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </ReportSection>
        )}

        {inspection.mite_wash && (
          <ReportSection title="Mite Monitoring">
            {inspection.mite_method && <DataRow label="Method" value={inspection.mite_method} />}
            {inspection.mite_count && <DataRow label="Mite Count (per 300 bees)" value={inspection.mite_count} />}
            {inspection.mite_percentage && <DataRow label="Mite Percentage" value={`${inspection.mite_percentage}%`} />}
            {inspection.mite_treatment && inspection.mite_treatment.length > 0 && (
              <DataRow label="Treatment Applied" value={inspection.mite_treatment.join(', ')} />
            )}
            {inspection.mite_application && inspection.mite_application.length > 0 && (
              <DataRow label="Application Method" value={inspection.mite_application.join(', ')} />
            )}
            {inspection.mite_dosage && <DataRow label="Dosage" value={inspection.mite_dosage} />}
          </ReportSection>
        )}

        {inspection.actions && inspection.actions.length > 0 && (
          <ReportSection title="Actions Taken">
            <div className="flex flex-wrap gap-2">
              {inspection.actions.map((action) => (
                <span key={action} className="bg-amber-100 text-amber-800 px-4 py-2 rounded-full font-medium">
                  {action}
                </span>
              ))}
            </div>
            {inspection.actions_other && (
              <div className="mt-4">
                <DataRow label="Other Actions" value={inspection.actions_other} />
              </div>
            )}
          </ReportSection>
        )}

        {inspection.actions_to_take && inspection.actions_to_take.length > 0 && (
          <ReportSection title="Tasks (Actions to Be Taken)">
            <div className="flex flex-wrap gap-2">
              {inspection.actions_to_take.map((action) => (
                <span key={action} className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full font-medium">
                  {action}
                </span>
              ))}
            </div>
            {inspection.actions_assigned_beekeeper && (
              <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2">
                  <span className="text-purple-700 font-medium">👤 Assigned to:</span>
                  <span className="text-purple-900 font-semibold">{inspection.actions_assigned_beekeeper}</span>
                </div>
              </div>
            )}
          </ReportSection>
        )}

        {inspection.notes && (
          <ReportSection title="Additional Notes">
            <p className="text-stone-700 leading-relaxed whitespace-pre-wrap">{inspection.notes}</p>
          </ReportSection>
        )}

        {inspection.photos && inspection.photos.length > 0 && (
          <ReportSection title="Photo Documentation">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {inspection.photos.map((photoPath, idx) => (
                <div key={idx} className="w-full h-48 rounded-xl border-2 border-stone-200 overflow-hidden">
                  <InspectionPhoto path={photoPath} />
                </div>
              ))}
            </div>
          </ReportSection>
        )}

        {inspection.videos && inspection.videos.length > 0 && (
          <ReportSection title="Video Documentation">
            <div className="space-y-2">
              {inspection.videos.map((videoId) => (
                <video
                  key={videoId}
                  src={`${API}/uploads/${videoId}`}
                  controls
                  className="w-full rounded-xl border-2 border-stone-200"
                />
              ))}
            </div>
          </ReportSection>
        )}
      </div>

    </div>
  );
};

const FRAME_LABELS = {
  'B': { label: 'Brood', color: 'bg-amber-100 text-amber-800' },
  'H': { label: 'Honey', color: 'bg-yellow-100 text-yellow-800' },
  'N': { label: 'Nectar', color: 'bg-lime-100 text-lime-800' },
  'P': { label: 'Pollen', color: 'bg-orange-100 text-orange-800' },
  'E': { label: 'Empty', color: 'bg-stone-100 text-stone-500' },
  'F': { label: 'Found.', color: 'bg-stone-50 text-stone-400' },
  'C': { label: 'Comb', color: 'bg-yellow-200 text-yellow-900' },
  'D': { label: 'Drone', color: 'bg-blue-100 text-blue-800' },
  'BB': { label: 'Bee Bread', color: 'bg-yellow-300 text-yellow-900' },
};

const FrameBoxDisplay = ({ label, frames }) => {
  const frameCount = frames.length;
  const gridCols = frameCount <= 8 ? `grid-cols-${frameCount}` : 'grid-cols-10';
  return (
    <div className="mb-4" data-testid={`frame-box-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="font-medium text-stone-800 mb-2">{label}</div>
      <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${Math.min(frameCount, 10)}, minmax(0, 1fr))` }}>
        {frames.map((frame, i) => {
          const info = FRAME_LABELS[frame] || null;
          return (
            <div key={i} className="text-center">
              <div className="text-xs text-stone-500 mb-1">{i + 1}</div>
              <div className={`rounded p-2 text-xs font-medium ${info ? info.color : 'bg-stone-100 text-stone-400'}`}>
                {info ? info.label : (frame || '-')}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const InfoCard = ({ icon, label, value }) => (
  <div className="flex items-center gap-3 bg-stone-50 rounded-xl p-4">
    <div className="text-amber-600">{icon}</div>
    <div>
      <div className="text-xs text-stone-500 font-medium uppercase">{label}</div>
      <div className="text-stone-800 font-semibold">{value}</div>
    </div>
  </div>
);

const ReportSection = ({ title, children }) => (
  <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
    <h2 className="text-xl font-bold text-stone-800 font-heading mb-4">{title}</h2>
    <div className="space-y-3">{children}</div>
  </div>
);

const DataRow = ({ label, value }) => (
  <div className="flex justify-between items-start py-2 border-b border-stone-100 last:border-0">
    <span className="text-stone-600 font-medium">{label}</span>
    <span className="text-stone-800 font-semibold text-right">{value}</span>
  </div>
);

export default InspectionView;
